import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  AvailabilitySchema,
  AvailabilityCreateInputSchema,
  AvailabilityUpdateInputSchema,
  type Availability,
  type AvailabilityCreateInput,
  type AvailabilityUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `availability` (règles de disponibilité, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`AvailabilityCreateInputSchema`) AVANT toute écriture Firestore. Les liens vers
 * d'autres familles (worker, resource, place) sont de simples ids. Les
 * réservations, shifts et absences pointent l'availability de leur côté ; on ne
 * les stocke pas ici.
 */

export type { AvailabilityCreateInput };

/** Nom de la collection Firestore de la famille availability. */
export const AVAILABILITIES_COLLECTION = 'availabilities';

/**
 * Repository typé pour la famille `availability`.
 * Toute lecture est validée par `AvailabilitySchema`, toute écriture par
 * `AvailabilityCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface AvailabilityRepository {
  create(input: AvailabilityCreateInput): Promise<Availability>;
  list(options?: ListOptions): Promise<Availability[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Availability | null>;
  update(id: string, patch: AvailabilityUpdateInput): Promise<Availability>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Availability>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Availability>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Availability>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const availabilityConverter = createConverter<Availability>((raw: DocumentData) => AvailabilitySchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createAvailabilityRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): AvailabilityRepository {
  const col = collection(db, AVAILABILITIES_COLLECTION).withConverter(availabilityConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Availability>(db, col, AVAILABILITIES_COLLECTION, getActorId);

  return {
    async create(input: AvailabilityCreateInput): Promise<Availability> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = AvailabilityCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.workerId !== undefined) optional['workerId'] = fields.workerId;
      if (fields.resourceId !== undefined) optional['resourceId'] = fields.resourceId;
      if (fields.placeId !== undefined) optional['placeId'] = fields.placeId;
      if (fields.weeklyRules !== undefined) optional['weeklyRules'] = fields.weeklyRules;
      if (fields.exceptions !== undefined) optional['exceptions'] = fields.exceptions;
      if (fields.validFrom !== undefined) optional['validFrom'] = fields.validFrom;
      if (fields.validTo !== undefined) optional['validTo'] = fields.validTo;
      if (fields.timezone !== undefined) optional['timezone'] = fields.timezone;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Availability);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (validFrom/validTo sont déjà des
      // Date issues de l'entrée validée).
      return AvailabilitySchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Availability[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Availability | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: AvailabilityUpdateInput): Promise<Availability> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = AvailabilityUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, AVAILABILITIES_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`availability ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
